/**
 * @file DistanceMeasurementTool.tsx
 * @description Interactive tool for measuring distances between elements in the Forge Viewer
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Ruler, AlertCircle } from 'lucide-react';
import { useViewerStore } from '@/store/viewerStore';
import { PropertiesService } from '@/services/core/propertiesTools';
import { toast } from '@/components/common/Toast';

// THREE is available globally when Forge Viewer is loaded
declare const THREE: any;

interface MeasurementResult {
  element1: {
    dbId: number;
    name: string;
    boundingBox: any; // THREE.Box3
    center: any; // THREE.Vector3
  };
  element2: {
    dbId: number;
    name: string;
    boundingBox: any; // THREE.Box3
    center: any; // THREE.Vector3
  };
  measurement: {
    type: 'center-to-center' | 'surface-to-surface' | 'center-to-surface';
    distanceInFeet: number;
    distanceInMeters: number;
    distanceInModel: number; // Raw model units
    calculationTime: number;
  };
  details?: {
    closestPoints?: { point1: any; point2: any }; // For surface measurements
    overlapping?: boolean;
    touching?: boolean;
  };
}

// Cache for bounding boxes to improve performance
const boundingBoxCache = new Map<number, any>();

export function DistanceMeasurementTool() {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [element1Id, setElement1Id] = useState('');
  const [element2Id, setElement2Id] = useState('');
  const [measurementType, setMeasurementType] = useState<'center-to-center' | 'surface-to-surface' | 'center-to-surface'>('surface-to-surface');
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState<MeasurementResult | null>(null);
  const [showVisualization, setShowVisualization] = useState(true);

  const { viewer, isModelLoaded } = useViewerStore();

  // Clear cache when model changes
  const clearCache = () => {
    boundingBoxCache.clear();
  };

  // Get cached bounding box or retrieve new one
  const getCachedBoundingBox = (dbId: number): any => {
    if (boundingBoxCache.has(dbId)) {
      const cached = boundingBoxCache.get(dbId);
      return cached;
    }

    
    // Method 1: Try using getNodeBox directly
    const box = new THREE.Box3();
    const instanceTree = viewer.model.getInstanceTree();
    
    if (instanceTree) {
      // First try the direct getNodeBox method
      try {
        const success = instanceTree.getNodeBox(dbId, box);
        if (success) {
          boundingBoxCache.set(dbId, box);
          return box;
        }
      } catch (e) {
      }
      
      // Method 2: Fallback to fragment enumeration
      let hasFragments = false;
      instanceTree.enumNodeFragments(dbId, (fragId: number) => {
        hasFragments = true;
        const fragList = viewer.model.getFragmentList();
        const fragBox = new THREE.Box3();
        fragList.getWorldBounds(fragId, fragBox);
        box.union(fragBox);
      }, true);
      
      if (hasFragments) {
        boundingBoxCache.set(dbId, box);
        return box;
      }
    }
    
    // Method 3: Try using selection as last resort
    viewer.select([dbId]);
    const selectionBox = viewer.utilities.getBoundingBox(true);
    viewer.clearSelection();
    
    if (selectionBox && !selectionBox.isEmpty()) {
      boundingBoxCache.set(dbId, selectionBox);
      return selectionBox;
    }

    return null;
  };

  // Convert element ID to DbId
  const resolveElementId = async (elementId: string): Promise<number | null> => {
    if (!elementId.trim()) return null;

    const id = elementId.trim();
    const numId = parseInt(id);

    if (!isNaN(numId)) {
      return numId;
    }

    // Try as Revit Element ID
    const dbId = await PropertiesService.findDbIdByRevitId(viewer, id);
    return dbId;
  };

  // Get element name
  const getElementName = async (dbId: number): Promise<string> => {
    try {
      const props = await PropertiesService.getElementProperties(viewer, dbId);
      const nameProp = props.find(p => 
        p.displayName === 'Name' || 
        p.displayName === 'Element Name' || 
        p.displayName === 'Type Name'
      );
      return nameProp?.displayValue as string || `Element_${dbId}`;
    } catch {
      return `Element_${dbId}`;
    }
  };

  // Calculate center-to-center distance
  const calculateCenterToCenter = (box1: any, box2: any): number => {
    const center1 = box1.getCenter(new THREE.Vector3());
    const center2 = box2.getCenter(new THREE.Vector3());
    return center1.distanceTo(center2);
  };

  // Calculate surface-to-surface distance
  const calculateSurfaceToSurface = (box1: any, box2: any): { distance: number; overlapping: boolean; touching: boolean } => {
    // Check if boxes overlap
    if (box1.intersectsBox(box2)) {
      return { distance: 0, overlapping: true, touching: false };
    }

    // Calculate separating axis distances
    const xDistance = Math.max(0, 
      Math.max(box2.min.x - box1.max.x, box1.min.x - box2.max.x)
    );
    const yDistance = Math.max(0,
      Math.max(box2.min.y - box1.max.y, box1.min.y - box2.max.y)
    );
    const zDistance = Math.max(0,
      Math.max(box2.min.z - box1.max.z, box1.min.z - box2.max.z)
    );

    const distance = Math.sqrt(xDistance * xDistance + yDistance * yDistance + zDistance * zDistance);
    const touching = distance < 0.001; // Consider touching if very close

    return { distance, overlapping: false, touching };
  };

  // Calculate center-to-surface distance
  const calculateCenterToSurface = (box1: any, box2: any): number => {
    const center1 = box1.getCenter(new THREE.Vector3());
    const closestPoint = new THREE.Vector3();
    box2.clampPoint(center1, closestPoint);
    return center1.distanceTo(closestPoint);
  };

  // Convert distance to feet
  const convertToFeet = (distance: number): number => {
    // Forge Viewer uses feet internally for many models
    // Check if model uses different units
    const unitScale = viewer.model?.getUnitScale() || 1;
    const unitString = viewer.model?.getUnitString() || 'feet';
    
    
    // If already in feet, return as is
    if (unitString.toLowerCase().includes('feet') || unitString.toLowerCase().includes('ft')) {
      return distance;
    }
    
    // Convert to meters first (unitScale converts to meters)
    const distanceInMeters = distance * unitScale;
    
    // Convert meters to feet
    const METERS_TO_FEET = 3.28084;
    return distanceInMeters * METERS_TO_FEET;
  };

  // Visualize measurement
  const visualizeMeasurement = (dbId1: number, dbId2: number, box1: any, box2: any, type: string) => {
    if (!showVisualization) return;

    // Clear previous visualizations
    viewer.clearThemingColors();
    viewer.overlays.clearScene('distance-measurement');

    // Highlight elements
    viewer.setThemingColor(dbId1, new THREE.Vector4(0, 0, 1, 0.6)); // Blue
    viewer.setThemingColor(dbId2, new THREE.Vector4(0, 1, 0, 0.6)); // Green

    // Add visualization based on measurement type
    if (type === 'center-to-center') {
      const center1 = box1.getCenter(new THREE.Vector3());
      const center2 = box2.getCenter(new THREE.Vector3());
      
      // Create line between centers
      const geometry = new THREE.BufferGeometry().setFromPoints([center1, center2]);
      const material = new THREE.LineBasicMaterial({ 
        color: 0xffff00, 
        linewidth: 3,
        opacity: 0.8,
        transparent: true
      });
      const line = new THREE.Line(geometry, material);
      
      viewer.overlays.addMesh(line, 'distance-measurement');
      
      // Add spheres at centers
      const sphereGeometry = new THREE.SphereGeometry(0.5, 16, 16);
      const sphereMaterial1 = new THREE.MeshBasicMaterial({ color: 0x0000ff, transparent: true, opacity: 0.8 });
      const sphereMaterial2 = new THREE.MeshBasicMaterial({ color: 0x00ff00, transparent: true, opacity: 0.8 });
      
      const sphere1 = new THREE.Mesh(sphereGeometry, sphereMaterial1);
      const sphere2 = new THREE.Mesh(sphereGeometry, sphereMaterial2);
      
      sphere1.position.copy(center1);
      sphere2.position.copy(center2);
      
      viewer.overlays.addMesh(sphere1, 'distance-measurement');
      viewer.overlays.addMesh(sphere2, 'distance-measurement');
    }

    // Force viewer update
    viewer.impl.invalidate(true, true, true);
    
    // Fit view to both elements
    viewer.fitToView([dbId1, dbId2], viewer.model, true);
  };

  // Handle measurement
  const handleMeasure = async () => {
    if (!viewer || !isModelLoaded) {
      toast.error('Viewer not ready. Please load a model first.');
      return;
    }

    if (!element1Id || !element2Id) {
      toast.warning('Please enter both element IDs');
      return;
    }

    setIsProcessing(true);
    const startTime = performance.now();

    try {

      // Resolve element IDs
      const dbId1 = await resolveElementId(element1Id);
      const dbId2 = await resolveElementId(element2Id);

      if (!dbId1 || !dbId2) {
        toast.error('Could not resolve one or both element IDs');
        return;
      }


      // Get element names
      const [name1, name2] = await Promise.all([
        getElementName(dbId1),
        getElementName(dbId2)
      ]);


      // Get bounding boxes
      const box1 = getCachedBoundingBox(dbId1);
      const box2 = getCachedBoundingBox(dbId2);

      if (!box1 || !box2) {
        toast.error('Could not get bounding boxes for elements');
        return;
      }
      
      if (box1.isEmpty() || box2.isEmpty()) {
        toast.error('One or both elements have empty bounding boxes');
        return;
      }


      // Calculate distance based on type
      let distanceInModel = 0;
      let overlapping = false;
      let touching = false;

      switch (measurementType) {
        case 'center-to-center':
          distanceInModel = calculateCenterToCenter(box1, box2);
          break;

        case 'surface-to-surface':
          const surfaceResult = calculateSurfaceToSurface(box1, box2);
          distanceInModel = surfaceResult.distance;
          overlapping = surfaceResult.overlapping;
          touching = surfaceResult.touching;
          break;

        case 'center-to-surface':
          distanceInModel = calculateCenterToSurface(box1, box2);
          break;
      }

      // Convert units
      const distanceInFeet = convertToFeet(distanceInModel);
      const distanceInMeters = distanceInFeet * 0.3048;


      // Calculate time taken
      const calculationTime = performance.now() - startTime;

      // Create result
      const measurementResult: MeasurementResult = {
        element1: {
          dbId: dbId1,
          name: name1,
          boundingBox: box1,
          center: box1.getCenter(new THREE.Vector3())
        },
        element2: {
          dbId: dbId2,
          name: name2,
          boundingBox: box2,
          center: box2.getCenter(new THREE.Vector3())
        },
        measurement: {
          type: measurementType,
          distanceInFeet,
          distanceInMeters,
          distanceInModel,
          calculationTime
        },
        details: {
          overlapping,
          touching
        }
      };

      setResult(measurementResult);

      // Visualize
      visualizeMeasurement(dbId1, dbId2, box1, box2, measurementType);

      toast.success(`Distance: ${distanceInFeet.toFixed(3)} ft`);

    } catch (error) {
      toast.error('Error during measurement. Check console for details.');
    } finally {
      setIsProcessing(false);
    }
  };

  // Export result as JSON
  const exportResult = () => {
    if (!result) return;

    const exportData = {
      timestamp: new Date().toISOString(),
      element1: {
        id: result.element1.dbId,
        name: result.element1.name,
        center: {
          x: result.element1.center.x,
          y: result.element1.center.y,
          z: result.element1.center.z
        }
      },
      element2: {
        id: result.element2.dbId,
        name: result.element2.name,
        center: {
          x: result.element2.center.x,
          y: result.element2.center.y,
          z: result.element2.center.z
        }
      },
      measurement: result.measurement,
      details: result.details
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `distance_measurement_${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Result exported as JSON');
  };

  return (
    <div className="mt-4 bg-dark-100 rounded-lg border border-gray-700">
      {/* Header */}
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-dark-200 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <Ruler className="w-5 h-5 text-cyan-400" />
          <span className="font-semibold text-gray-100">Distance Measurement Tool</span>
          <span className="text-xs text-white">(Debug)</span>
        </div>
        <div className="flex items-center gap-2">
          {!isCollapsed && (
            <span className="text-xs text-white">
              {isModelLoaded ? '✓ Model Loaded' : '⚠ No Model'}
            </span>
          )}
          {isCollapsed ? (
            <ChevronRight className="w-5 h-5 text-white" />
          ) : (
            <ChevronDown className="w-5 h-5 text-white" />
          )}
        </div>
      </button>

      {/* Content */}
      {!isCollapsed && (
        <div className="px-4 pb-4 border-t border-gray-700">
          <div className="mt-4 space-y-4">
            {/* Input Fields */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-white mb-1">
                  Element 1 (DbId or RevitId)
                </label>
                <input
                  type="text"
                  value={element1Id}
                  onChange={(e) => setElement1Id(e.target.value)}
                  placeholder="e.g., 1234 or 385699"
                  className="w-full px-3 py-2 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-cyan-500 focus:outline-none text-sm"
                />
              </div>

              <div>
                <label className="block text-xs text-white mb-1">
                  Element 2 (DbId or RevitId)
                </label>
                <input
                  type="text"
                  value={element2Id}
                  onChange={(e) => setElement2Id(e.target.value)}
                  placeholder="e.g., 5678 or 385700"
                  className="w-full px-3 py-2 bg-dark-200 text-gray-100 rounded border border-gray-600 focus:border-cyan-500 focus:outline-none text-sm"
                />
              </div>
            </div>

            {/* Measurement Type */}
            <div>
              <label className="block text-xs text-white mb-2">
                Measurement Type
              </label>
              <div className="grid grid-cols-3 gap-2">
                <button
                  onClick={() => setMeasurementType('center-to-center')}
                  className={`px-3 py-2 rounded text-xs transition-colors ${
                    measurementType === 'center-to-center'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-dark-200 text-gray-300 hover:bg-dark-300'
                  }`}
                >
                  Center to Center
                </button>
                <button
                  onClick={() => setMeasurementType('surface-to-surface')}
                  className={`px-3 py-2 rounded text-xs transition-colors ${
                    measurementType === 'surface-to-surface'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-dark-200 text-gray-300 hover:bg-dark-300'
                  }`}
                >
                  Surface to Surface
                </button>
                <button
                  onClick={() => setMeasurementType('center-to-surface')}
                  className={`px-3 py-2 rounded text-xs transition-colors ${
                    measurementType === 'center-to-surface'
                      ? 'bg-cyan-600 text-white'
                      : 'bg-dark-200 text-gray-300 hover:bg-dark-300'
                  }`}
                >
                  Center to Surface
                </button>
              </div>
            </div>

            {/* Options */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-gray-300">
                <input
                  type="checkbox"
                  checked={showVisualization}
                  onChange={(e) => setShowVisualization(e.target.checked)}
                  className="w-4 h-4"
                />
                Show visualization
              </label>
              
              <button
                onClick={clearCache}
                className="text-xs text-white hover:text-gray-200 transition-colors"
              >
                Clear cache
              </button>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <button
                onClick={handleMeasure}
                disabled={isProcessing || !isModelLoaded}
                className={`flex-1 px-4 py-2 rounded font-medium transition-colors ${
                  isProcessing || !isModelLoaded
                    ? 'bg-gray-600 text-white cursor-not-allowed'
                    : 'bg-cyan-600 hover:bg-cyan-700 text-white'
                }`}
              >
                {isProcessing ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="animate-spin">⟳</span>
                    Measuring...
                  </span>
                ) : (
                  'Measure Distance'
                )}
              </button>

              {result && (
                <button
                  onClick={exportResult}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium transition-colors"
                >
                  Export
                </button>
              )}
            </div>

            {/* Result Display */}
            {result && (
              <div className="mt-4 p-3 bg-dark-200 rounded border border-gray-600">
                <h4 className="text-sm font-semibold text-gray-100 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-cyan-400" />
                  Measurement Result
                </h4>
                
                <div className="space-y-2 text-xs">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-white">Element 1:</span>
                      <div className="text-gray-200">
                        {result.element1.name} (ID: {result.element1.dbId})
                      </div>
                    </div>
                    <div>
                      <span className="text-white">Element 2:</span>
                      <div className="text-gray-200">
                        {result.element2.name} (ID: {result.element2.dbId})
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-600 pt-2">
                    <div className="text-white">Measurement Type:</div>
                    <div className="text-gray-200 capitalize">
                      {result.measurement.type.replace('-', ' ')}
                    </div>
                  </div>

                  <div className="border-t border-gray-600 pt-2">
                    <div className="text-white">Distance:</div>
                    <div className="space-y-1">
                      <div className="text-cyan-400 text-lg font-semibold">
                        {result.measurement.distanceInFeet.toFixed(3)} feet
                      </div>
                      <div className="text-gray-300">
                        {result.measurement.distanceInMeters.toFixed(3)} meters
                      </div>
                      <div className="text-gray-500">
                        Model units: {result.measurement.distanceInModel.toFixed(6)}
                      </div>
                    </div>
                  </div>

                  {result.details && (
                    <div className="border-t border-gray-600 pt-2">
                      <div className="text-white">Status:</div>
                      <div className="flex gap-3">
                        {result.details.overlapping && (
                          <span className="text-orange-400">⚠ Overlapping</span>
                        )}
                        {result.details.touching && (
                          <span className="text-yellow-400">✓ Touching</span>
                        )}
                        {!result.details.overlapping && !result.details.touching && (
                          <span className="text-green-400">↔ Separated</span>
                        )}
                      </div>
                    </div>
                  )}

                  <div className="border-t border-gray-600 pt-2 text-gray-500">
                    Calculation time: {result.measurement.calculationTime.toFixed(2)}ms
                  </div>
                </div>
              </div>
            )}

            {/* Debug Info */}
            <div className="mt-3 p-2 bg-dark-200 rounded text-xs text-white">
              <div>Viewer Status: {viewer ? '✓ Initialized' : '✗ Not Ready'}</div>
              <div>Model Status: {isModelLoaded ? '✓ Loaded' : '✗ Not Loaded'}</div>
              {viewer?.model && (
                <>
                  <div>Model Units: {viewer.model.getUnitString()}</div>
                  <div>Unit Scale: {viewer.model.getUnitScale()}</div>
                  <div>Cache Size: {boundingBoxCache.size} elements</div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}